import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import LoadingSpinner from '../interfaz-usuario/carga/SpinnerCarga'
import { esUsuarioPanelGeneral, obtenerRutaPanelPorUsuario } from '../../utils/authRouting'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?:
    | 'admin'
    | 'user'
    | 'institucion'
    | 'institucion_jefe'
    | 'institucion_usuario'
    | 'institucion_alumno'
    | 'institucion_maestro'
    | 'institucion_admin'
    | 'institucion_supervisor'
    | 'empresa'
    | 'empresa_gestion'
    | 'empresa_admin'
    | 'empresa_moderador'
    | 'empresa_supervisor'
    | 'empresa_contador'
    | 'empresa_usuario'
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, isLoading, usuario } = useAuth()
  const location = useLocation()
  const rolGlobal = usuario?.rol_global
  const rolOrganizacion = usuario?.rol_organizacion
  const tipoOrganizacion = usuario?.tipo_organizacion
  const empresaRolesGestion = ['admin', 'moderador', 'supervisor', 'contador']
  const empresaRolesAdministracion = ['admin', 'moderador']
  const empresaRoles = [...empresaRolesGestion, 'usuario']
  const institucionRolesGestion = ['admin', 'supervisor', 'jefe', 'maestro']
  const institucionRolesUsuario = ['usuario', 'alumno']
  const esEmpresa = tipoOrganizacion === 'empresa'
  const esInstitucion = tipoOrganizacion === 'institucion'
  const esEmpresaGestion = esEmpresa && empresaRolesGestion.includes(rolOrganizacion || '')
  const esEmpresaAdministracion = esEmpresa && empresaRolesAdministracion.includes(rolOrganizacion || '')
  const esEmpresaUsuario = esEmpresa && empresaRoles.includes(rolOrganizacion || '')
  const esInstitucionGestion = esInstitucion && institucionRolesGestion.includes(rolOrganizacion || '')
  const esInstitucionUsuario = esInstitucion && institucionRolesUsuario.includes(rolOrganizacion || '')
  const requiereOrganizacionEmpresa = ['empresa', 'empresa_gestion', 'empresa_admin', 'empresa_usuario'].includes(requiredRole || '')
  const tieneContextoEmpresa = esEmpresaUsuario && typeof usuario?.id_organizacion === 'number'
  const rutaPanelUsuario = obtenerRutaPanelPorUsuario(usuario)

  // Mostrar spinner mientras se verifica la autenticación
  if (isLoading) {
    return <LoadingSpinner />
  }

  // Redirigir a login si no está autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requiereOrganizacionEmpresa && !tieneContextoEmpresa) {
    return <Navigate to={rutaPanelUsuario} replace />
  }

  // Verificar rol si es requerido
  if (requiredRole) {
    // Superadmin tiene acceso universal a cualquier ruta protegida
    if (rolGlobal === 'superadmin') {
      return <>{children}</>
    }

    if (requiredRole === 'admin') {
      if (rolGlobal === 'admin') {
        return <>{children}</>
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'user') {
      if (esUsuarioPanelGeneral(usuario)) {
        return <>{children}</>
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'empresa' || requiredRole === 'empresa_usuario') {
      if (esEmpresaUsuario) {
        return <>{children}</>
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'empresa_gestion') {
      if (esEmpresaGestion) {
        return <>{children}</>
      }
      if (esEmpresaUsuario) {
        return <Navigate to="/empresa" replace />
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'empresa_admin') {
      if (esEmpresaAdministracion) {
        return <>{children}</>
      }
      if (esEmpresaUsuario) {
        return <Navigate to="/empresa" replace />
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'institucion') {
      if (esInstitucionGestion) {
        return <Navigate to="/institucion/jefes" replace />
      }
      if (esInstitucionUsuario) {
        return <Navigate to="/institucion/usuarios" replace />
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'institucion_jefe') {
      if (esInstitucion && ['jefe', 'maestro', 'admin', 'supervisor'].includes(rolOrganizacion || '')) {
        return <>{children}</>
      }
      if (esInstitucionUsuario) {
        return <Navigate to="/institucion/usuarios" replace />
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }

    if (requiredRole === 'institucion_usuario') {
      if (esInstitucion && ['usuario', 'alumno', 'jefe', 'maestro', 'admin', 'supervisor'].includes(rolOrganizacion || '')) {
        return <>{children}</>
      }
      if (esEmpresaUsuario) {
        return <Navigate to="/empresa" replace />
      }
      return <Navigate to={rutaPanelUsuario} replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
